import { useState } from 'react'
import { deletePaymentMethod } from '../services/integrations'
import { useAuth } from '../modules/auth'

export type DeletePaymentMethodErrorCode =
    | 'PAYMENT_METHOD_IN_USE'
    | 'NOT_FOUND'
    | 'FORBIDDEN'
    | 'GENERIC'

export interface DeletePaymentMethodResult {
    ok: boolean
    code?: DeletePaymentMethodErrorCode
    message?: string
}

const useDeletePaymentMethod = (): {
    deleteHandler: (pmId: string) => Promise<DeletePaymentMethodResult>
    loadingId: string | null
} => {
    const [loadingId, setLoadingId] = useState<string | null>(null)
    const { currentUser } = useAuth()
    const organizationId = currentUser?.organizationId
    const hasOrganization = currentUser?.hasOrganization

    const deleteHandler = async (pmId: string): Promise<DeletePaymentMethodResult> => {
        try {
            setLoadingId(pmId)
            const orgId = hasOrganization ? organizationId : undefined
            await deletePaymentMethod(pmId, orgId)
            return { ok: true }
        } catch (err: any) {
            const status: number | undefined = err?.response?.status
            const body = err?.response?.data
            if (status === 409 && body?.code === 'PAYMENT_METHOD_IN_USE') {
                return {
                    ok: false,
                    code: 'PAYMENT_METHOD_IN_USE',
                    message:
                        body?.error ??
                        'This card is currently backing your subscription. Set a different card as primary first.',
                }
            }
            if (status === 404) {
                return { ok: false, code: 'NOT_FOUND', message: body?.message ?? 'Card not found.' }
            }
            if (status === 403) {
                return { ok: false, code: 'FORBIDDEN', message: body?.message ?? 'Access denied.' }
            }
            return {
                ok: false,
                code: 'GENERIC',
                message: body?.message ?? 'Could not remove card.',
            }
        } finally {
            setLoadingId(null)
        }
    }

    return { deleteHandler, loadingId }
}

export default useDeletePaymentMethod
